import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Crown, TrendingUp, Eye, Target, Users, DollarSign, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import GlobalCounters from "@/components/GlobalCounters";
import JackpotBanner from "@/components/JackpotBanner";
import HallOfFame from "@/components/HallOfFame";
import LeaderboardCard from "@/components/LeaderboardCard";
import SubmitBidModal from "@/components/SubmitBidModal";
import AuthModal from "@/components/AuthModal";
import JoinHuntModal from "@/components/JoinHuntModal";
import RegisterProfileModal from "@/components/RegisterProfileModal";
import TopHunters from "@/components/TopHunters";
import HunterDashboard from "@/components/HunterDashboard";
import RulesSection from "@/components/RulesSection";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";

export default function Home() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [hallOfFame, setHallOfFame] = useState([]);
  const [huntersRefresh, setHuntersRefresh] = useState(0);
  const [hunter, setHunter] = useState(null);
  const [bidOpen, setBidOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [huntOpen, setHuntOpen] = useState(false);
  const [targetProfile, setTargetProfile] = useState(null);
  const [defaultTier, setDefaultTier] = useState("push_up");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [p, s, h] = await Promise.all([
        api.get("/profiles?limit=20"),
        api.get("/stats"),
        api.get("/hall-of-fame"),
      ]);
      setProfiles(p.data);
      setStats(s.data);
      setHallOfFame(h.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadHunter = async () => {
    if (!user) {
      setHunter(null);
      return;
    }
    try {
      const { data } = await api.get("/hunters/me");
      setHunter(data.hunter);
    } catch {
      setHunter(null);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    loadHunter();
  }, [user]);

  const handleSubmitBid = () => {
    if (!user) {
      toast.info("Log in primero para registrar tu perfil");
      setAuthOpen(true);
      return;
    }
    setRegisterOpen(true);
  };

  const handleOutbid = (profile) => {
    if (!user) {
      toast.info("Log in para pujar");
      setAuthOpen(true);
      return;
    }
    setTargetProfile(profile);
    setDefaultTier("push_up");
    setBidOpen(true);
  };

  const handleClick = async (profile) => {
    try {
      const { data } = await api.post(`/profiles/${profile.id}/click`);
      if (data.hunter_scored) {
        toast.success("+1 click único de Hunter", { duration: 1600 });
        setHuntersRefresh((k) => k + 1);
        loadHunter();
      }
      load();
    } catch {}
  };

  const openHunt = () => {
    if (!user) {
      toast.info("Log in para unirte a la caza");
      setAuthOpen(true);
      return;
    }
    setHuntOpen(true);
  };

  return (
    <div className="min-h-screen font-body relative">
      <Header
        onSubmit={handleSubmitBid}
        onOpenAuth={() => setAuthOpen(true)}
        onOpenHunt={openHunt}
        isHunter={!!hunter}
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-24 relative">
        {/* Jackpot Banner - always at very top */}
        <JackpotBanner />

        {/* Hero */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-neon-cyan/10 border border-neon-cyan/30 text-neon-cyan text-xs font-mono uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-ticker-blink" />
                SUBASTA EN VIVO
              </span>
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                Concurso · 72h por ronda
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
              Puja por tu <span className="shine-text">perfil</span>.
              <br />
              Gana <span className="text-neon-cyan">seguidores</span> y{" "}
              <span className="text-neon-red">efectivo</span>.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Tres formas de ganar en un solo mercado en vivo: crece tu audiencia, corónate como el
              creador #1 de la ronda, o vuélvete el mejor viralizador. El pozo se reparte cada 72 horas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                data-testid="hero-submit-bid-button"
                onClick={handleSubmitBid}
                className="bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold text-base px-6 py-6 rounded-full neon-cyan-glow"
              >
                <Zap className="w-4 h-4 mr-2" /> Registra tu Perfil
              </Button>
              <Button
                data-testid="hero-join-hunt-button"
                onClick={openHunt}
                variant="outline"
                className="border-neon-red/60 text-neon-red hover:bg-neon-red/10 hover:text-neon-red font-bold text-base px-6 py-6 rounded-full"
              >
                <Target className="w-4 h-4 mr-2" />
                {hunter ? "Mi Hunter" : "Únete a la Caza"}
              </Button>
              <a
                href="#leaderboard"
                data-testid="hero-view-leaderboard-link"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/10 hover:border-neon-cyan/50 text-sm font-mono uppercase tracking-widest transition-colors"
              >
                <TrendingUp className="w-4 h-4" /> Ver Top 20
              </a>
            </div>
          </motion.div>

          {/* THREE BENEFITS */}
          <motion.div
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
            }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10"
          >
            {/* BENEFIT 1: FOLLOWERS */}
            <motion.article
              data-testid="benefit-followers"
              variants={{
                hidden: { opacity: 0, y: 16 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
              className="relative rounded-2xl border border-neon-cyan/20 bg-void-card p-6 overflow-hidden group hover:border-neon-cyan/50 transition-colors"
            >
              <div
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-20 group-hover:opacity-40 transition-opacity"
                style={{ background: "radial-gradient(circle, #00F0FF, transparent 60%)" }}
                aria-hidden
              />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-neon-cyan/15 border border-neon-cyan/40 flex items-center justify-center text-neon-cyan">
                    <Users className="w-4 h-4" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon-cyan">
                    Beneficio 01
                  </span>
                </div>
                <h3 className="font-heading font-black text-xl leading-tight mb-2">
                  Multiplica tus <span className="text-neon-cyan">seguidores</span>
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Registra tu perfil de Instagram, TikTok, X, YouTube, Facebook o Twitch y puja
                  para escalar al Top 20 del día. Miles de visitantes verán tu link "Follow" y
                  se sumarán a tu comunidad.
                </p>
                <ul className="space-y-1.5 text-xs font-mono text-white/80">
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-cyan" />
                    Tu perfil visible 24/7 hasta el cierre
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-cyan" />
                    Botón "Follow" con tracking real de clicks
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-cyan" />
                    Más pujas = mejor posición = más tráfico
                  </li>
                </ul>
                <button
                  data-testid="benefit-followers-cta"
                  onClick={handleSubmitBid}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-neon-cyan/10 hover:bg-neon-cyan/20 border border-neon-cyan/40 text-neon-cyan text-xs font-mono uppercase tracking-widest transition-colors"
                >
                  <Zap className="w-3.5 h-3.5" /> Registrar mi perfil
                </button>
              </div>
            </motion.article>

            {/* BENEFIT 2: KING JACKPOT 50% */}
            <motion.article
              data-testid="benefit-king"
              variants={{
                hidden: { opacity: 0, y: 16 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
              className="relative rounded-2xl border border-neon-gold/30 bg-void-card p-6 overflow-hidden group hover:border-neon-gold/70 transition-colors"
            >
              <div
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-25 group-hover:opacity-45 transition-opacity"
                style={{ background: "radial-gradient(circle, #FFB800, transparent 60%)" }}
                aria-hidden
              />
              <div className="absolute top-3 right-3 px-2.5 py-0.5 rounded-full bg-neon-gold/15 border border-neon-gold/40 text-neon-gold text-[10px] font-mono uppercase tracking-widest">
                50%
              </div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-neon-gold/15 border border-neon-gold/40 flex items-center justify-center text-neon-gold">
                    <Crown className="w-4 h-4" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon-gold">
                    Beneficio 02
                  </span>
                </div>
                <h3 className="font-heading font-black text-xl leading-tight mb-2">
                  Sé el <span className="shine-text">King</span> del día
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Si al cierre del concurso (72h) tu perfil está en el ranking #1, te llevas
                  el <span className="text-neon-gold font-bold">50% del pozo total acumulado</span>.
                  Pagado directo a tu PayPal o cuenta bancaria.
                </p>
                <ul className="space-y-1.5 text-xs font-mono text-white/80">
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-gold" />
                    Empates favorecen al perfil más antiguo
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-gold" />
                    Anti-sniping: +60s si alguien puja en los últimos 2 min
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-gold" />
                    Payout wallet cifrado. Trono nuevo cada día
                  </li>
                </ul>
                <button
                  data-testid="benefit-king-cta"
                  onClick={handleSubmitBid}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-neon-gold/10 hover:bg-neon-gold/20 border border-neon-gold/50 text-neon-gold text-xs font-mono uppercase tracking-widest transition-colors"
                >
                  <DollarSign className="w-3.5 h-3.5" /> Pujar para ganar
                </button>
              </div>
            </motion.article>

            {/* BENEFIT 3: HUNTER 10% */}
            <motion.article
              data-testid="benefit-hunter"
              variants={{
                hidden: { opacity: 0, y: 16 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
              className="relative rounded-2xl border border-neon-red/25 bg-void-card p-6 overflow-hidden group hover:border-neon-red/60 transition-colors"
            >
              <div
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-25 group-hover:opacity-45 transition-opacity"
                style={{ background: "radial-gradient(circle, #FF003C, transparent 60%)" }}
                aria-hidden
              />
              <div className="absolute top-3 right-3 px-2.5 py-0.5 rounded-full bg-neon-red/15 border border-neon-red/40 text-neon-red text-[10px] font-mono uppercase tracking-widest">
                10%
              </div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-neon-red/15 border border-neon-red/40 flex items-center justify-center text-neon-red">
                    <Target className="w-4 h-4" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon-red">
                    Beneficio 03
                  </span>
                </div>
                <h3 className="font-heading font-black text-xl leading-tight mb-2">
                  Gana como <span className="text-neon-red">Viralizador</span>
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  No necesitas pagar para ganar. Únete como <span className="text-neon-red font-bold">Hunter</span>,
                  comparte perfiles por WhatsApp y gana el{" "}
                  <span className="text-neon-red font-bold">10% del pozo</span> si eres el que más
                  clicks únicos genera.
                </p>
                <ul className="space-y-1.5 text-xs font-mono text-white/80">
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-red" />
                    Botón WhatsApp con link único por perfil
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-red" />
                    Cada click único = 1 viral point (anti-fraude IP)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-neon-red" />
                    Gratis. Solo email y payout wallet
                  </li>
                </ul>
                <button
                  data-testid="benefit-hunter-cta"
                  onClick={openHunt}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-neon-red/10 hover:bg-neon-red/20 border border-neon-red/50 text-neon-red text-xs font-mono uppercase tracking-widest transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  {hunter ? "Ver mi caza" : "Convertirme en Hunter"}
                </button>
              </div>
            </motion.article>
          </motion.div>

          <GlobalCounters stats={stats} />
        </section>

        {/* Hall of Fame */}
        <HallOfFame entries={hallOfFame} />

        {/* Hunter Dashboard - visible always so newcomers see the mechanic */}
        <HunterDashboard hunter={hunter} refreshKey={huntersRefresh} />

        {/* Official Rules */}
        <RulesSection />

        {/* Leaderboard + Top Hunters */}
        <section id="leaderboard" className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.3em] text-neon-cyan mb-2">
                  LEADERBOARD · TOP 20
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
                  La batalla de hoy
                </h2>
              </div>
              <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                <Eye className="w-3.5 h-3.5" />
                <span>Auto-refresh cada 5s</span>
              </div>
            </div>

            {loading ? (
              <div className="text-muted-foreground py-16 text-center font-mono">Cargando...</div>
            ) : profiles.length === 0 ? (
              <div className="border border-dashed border-white/10 rounded-2xl p-12 text-center">
                <Crown className="w-10 h-10 mx-auto text-neon-gold mb-4 opacity-60" />
                <div className="text-lg font-heading font-bold mb-2">El trono está vacío.</div>
                <p className="text-muted-foreground text-sm mb-6">
                  Sé el primero en registrarte y pujar. Toma la lista al instante.
                </p>
                <Button
                  data-testid="empty-submit-bid-button"
                  onClick={handleSubmitBid}
                  className="bg-neon-gold hover:bg-neon-gold/90 text-black font-bold rounded-full"
                >
                  <Crown className="w-4 h-4 mr-2" /> Registrar mi perfil
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {profiles.map((p, idx) => (
                  <LeaderboardCard
                    key={p.id}
                    profile={p}
                    rank={idx + 1}
                    hunter={hunter}
                    onOutbid={() => handleOutbid(p)}
                    onClick={() => handleClick(p)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-1 space-y-6">
            <TopHunters refreshKey={huntersRefresh} />
            {hunter && (
              <div className="rounded-2xl border border-neon-red/20 bg-void-card p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-neon-red mb-2">
                  Your Hunter
                </div>
                <div className="text-sm font-mono truncate mb-3">{hunter.email}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-3xl font-mono font-bold text-[#25D366]">
                      {hunter.viral_points_today ?? 0}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                      viral points
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl font-mono font-bold text-neon-red">
                      {hunter.unique_clicks_today ?? 0}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                      unique clicks
                    </div>
                  </div>
                </div>
                <Button
                  data-testid="edit-hunter-button"
                  onClick={openHunt}
                  variant="outline"
                  size="sm"
                  className="mt-4 w-full border-white/15 text-xs"
                >
                  Editar wallet
                </Button>
              </div>
            )}
          </div>
        </section>

        <p className="mt-8 text-xs font-mono text-muted-foreground text-center uppercase tracking-widest">
          Todos los links abren en pestaña nueva · Las pujas más altas ganan visibilidad
        </p>
      </main>

      <SubmitBidModal
        open={bidOpen}
        onClose={() => setBidOpen(false)}
        targetProfile={targetProfile}
        defaultTier={defaultTier}
        onCompleted={load}
      />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <JoinHuntModal
        open={huntOpen}
        onClose={() => setHuntOpen(false)}
        existingHunter={hunter}
        onRegistered={(h) => {
          setHunter(h);
          setHuntersRefresh((k) => k + 1);
        }}
      />
      <RegisterProfileModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onOpenBidModal={(profile) => {
          load();
          setTargetProfile(profile);
          setDefaultTier("god_mode");
          setBidOpen(true);
        }}
      />

      <LegalFooter />
    </div>
  );
}
